import { useEffect, useState } from "react";
import axios from "axios";
import {
  Download,
  CalendarDays,
  X,
  Mail,
  User as UserIcon,
  Loader2,
  Check,
  Users,
  Flame,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVER_IMG = "/CAPA_HYROX_THUMB.jpg";
const BOOKLET_PDF = "/TREINOS_EXTRA_HYROX.pdf";

const TREINOS = [
  { codigo: "01", titulo: "EMOM Peso Corporal", meta: "20 min · Sem equipamento", file: "/TREINO_HYROX_1_EMOM.pdf" },
  { codigo: "02", titulo: "Escada Descendente", meta: "Resistência muscular", file: "/TREINO_HYROX_2_ESCADA.pdf" },
  { codigo: "03", titulo: "Intervalos de Corrida", meta: "6 × 400 m · Motor aeróbio", file: "/TREINO_HYROX_3_CORRIDA.pdf" },
  { codigo: "04", titulo: "Core & Estabilidade", meta: "15 min · Recuperação", file: "/TREINO_HYROX_4_CORE.pdf" },
  { codigo: "05", titulo: "Força", meta: "Halteres ou mochila", file: "/TREINO_HYROX_5_FORCA.pdf" },
];

// Visível SÓ a partir de 17 de Agosto (inclusive) até 31 de Agosto (Agosto = mês 7 em getMonth).
export function isHyroxActive() {
  const now = new Date();
  return now.getMonth() === 7 && now.getDate() >= 17;
}

function DownloadModal({ open, onClose, onSuccess, selectedFile, selectedLabel }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setNome(""); setEmail(""); setError(""); setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!nome.trim()) return setError("Diz-me como te chamas.");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError("Verifica o teu email.");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/hyrox/request`, {
        nome: nome.trim(),
        email: email.trim().toLowerCase(),
      });
      onSuccess(res.data?.downloads || null);
      const link = document.createElement("a");
      link.href = selectedFile || BOOKLET_PDF;
      link.download = "";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível processar. Tenta de novo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="hyrox-modal"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border border-neutral-800 max-w-md w-full p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-yellow-400 transition-colors"
          data-testid="hyrox-modal-close"
          aria-label="Fechar"
        >
          <X size={22} />
        </button>

        <div className="flex items-center gap-2 text-yellow-400 text-[10px] uppercase tracking-[0.32em] font-black mb-3">
          <span className="w-6 h-[2px] bg-yellow-400" />
          <Flame size={12} />
          <span>Hyrox em Férias</span>
        </div>

        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tighter leading-tight mb-2">
          Recebe o teu <br />
          <span className="text-yellow-400">{selectedLabel || "booklet Hyrox"}</span>
        </h3>
        <p className="text-neutral-400 text-sm mb-6 leading-relaxed">
          Preenche abaixo e enviamos-te o PDF para o email. A descarga começa
          também imediatamente.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-2 flex items-center gap-2">
              <UserIcon size={12} /> O teu nome
            </div>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-yellow-400 outline-none text-white px-4 py-3 text-sm transition-colors"
              placeholder="Ex.: João Silva"
              data-testid="hyrox-form-nome"
              autoFocus
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-2 flex items-center gap-2">
              <Mail size={12} /> Email
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-yellow-400 outline-none text-white px-4 py-3 text-sm transition-colors"
              placeholder="joao@exemplo.com"
              data-testid="hyrox-form-email"
            />
          </div>

          {error ? (
            <div className="text-red-400 text-xs" data-testid="hyrox-form-error">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-3 transition-colors"
            data-testid="hyrox-form-submit"
          >
            {saving ? (
              <><Loader2 size={16} className="animate-spin" /> A preparar…</>
            ) : (
              <><Download size={16} /> Descarregar agora</>
            )}
          </button>

          <div className="text-[10px] uppercase tracking-widest text-neutral-500 text-center pt-2">
            Prometemos zero spam · só o essencial
          </div>
        </form>
      </div>
    </div>
  );
}

function SuccessToast({ show, downloads, onClose }) {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 bg-neutral-950 border border-yellow-400/60 shadow-2xl max-w-sm p-5"
      data-testid="hyrox-toast"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex items-center justify-center bg-yellow-400 text-black flex-shrink-0">
          <Check size={20} strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-sm uppercase tracking-wider mb-1">
            Treino Hyrox a caminho!
          </div>
          <div className="text-neutral-400 text-xs leading-relaxed">
            A descarga começou e enviámos também o PDF para o teu email.
          </div>
          {downloads ? (
            <div className="text-yellow-400 text-[10px] uppercase tracking-widest mt-2 font-bold">
              Já somos {downloads} nesta edição Hyrox ⚡
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function HyroxSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(BOOKLET_PDF);
  const [selectedLabel, setSelectedLabel] = useState("Booklet Hyrox");
  const [downloads, setDownloads] = useState(0);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!isHyroxActive()) return;
    let mounted = true;
    axios
      .get(`${API}/hyrox/stats`)
      .then((r) => {
        if (mounted) setDownloads(r.data?.downloads || 0);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  if (!isHyroxActive()) return null;

  const openWith = (file, label) => {
    setSelectedFile(file);
    setSelectedLabel(label);
    setModalOpen(true);
  };

  const onSuccess = (newCount) => {
    if (newCount != null) setDownloads(newCount);
    setModalOpen(false);
    setShowToast(true);
  };

  return (
    <>
      <section
        id="hyrox-ferias"
        className="relative px-6 md:px-12 lg:px-24 py-16 md:py-24 border-t border-neutral-900 overflow-hidden"
        data-testid="hyrox-section"
      >
        <div
          className="pointer-events-none absolute -top-32 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-[0.12]"
          style={{ background: "radial-gradient(circle, #ffd60a 0%, transparent 65%)" }}
          aria-hidden="true"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Content — left side on desktop */}
          <div className="order-1">
            <div className="flex items-center gap-3 text-yellow-400 text-[10px] uppercase tracking-[0.32em] font-black mb-4">
              <span className="w-8 h-[2px] bg-yellow-400" />
              <Flame size={14} />
              <span>Treinos Extra · Edição Hyrox</span>
            </div>

            <h2
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tighter uppercase text-white"
              data-testid="hyrox-title"
            >
              Hyrox <br />
              <span className="text-yellow-400">em Férias!</span>
            </h2>

            <p
              className="mt-6 text-neutral-300 text-base md:text-lg max-w-xl leading-relaxed"
              data-testid="hyrox-subtitle"
            >
              5 treinos baseados no formato HYROX que podes fazer em qualquer
              lado — com ou sem material. Treina como um atleta, mesmo em férias.
            </p>

            <div className="mt-6 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">EMOM</span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">Ladder</span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">Corrida</span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">Core</span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">Força</span>
            </div>

            <button
              type="button"
              onClick={() => openWith(BOOKLET_PDF, "Booklet Hyrox Completo")}
              className="group mt-8 inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm px-7 py-4 transition-colors"
              data-testid="hyrox-download-booklet"
            >
              <Download size={18} />
              <span>Descarregar Booklet Hyrox</span>
              <span className="text-black/50 text-[10px] tracking-widest">PDF · 7 pág</span>
            </button>

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.32em] text-neutral-500 font-black mb-3">
                Ou descarrega cada treino
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {TREINOS.map((t) => (
                  <button
                    key={t.codigo}
                    type="button"
                    onClick={() => openWith(t.file, t.titulo)}
                    className="group border border-neutral-800 hover:border-yellow-400/70 bg-neutral-950/60 hover:bg-neutral-950 transition-all p-4 flex items-center gap-3 text-left"
                    data-testid={`hyrox-download-${t.codigo}`}
                  >
                    <div className="text-lg font-black text-yellow-400 leading-none w-8">
                      {t.codigo}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-bold text-[13px] leading-tight">
                        {t.titulo}
                      </div>
                      <div className="text-neutral-500 text-[10px] uppercase tracking-widest mt-1">
                        {t.meta}
                      </div>
                    </div>
                    <Download
                      size={16}
                      className="text-neutral-500 group-hover:text-yellow-400 transition-colors flex-shrink-0"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-neutral-500 text-xs tracking-wide">
              <div className="flex items-center gap-2">
                <CalendarDays size={14} className="text-yellow-400" />
                <span>Bónus de Agosto · Sessão experimental grátis no fim</span>
              </div>
              {downloads > 0 ? (
                <div
                  className="flex items-center gap-2"
                  data-testid="hyrox-downloads-count"
                >
                  <Users size={14} className="text-yellow-400" />
                  <span>
                    <b className="text-yellow-400">{downloads}</b>{" "}
                    {downloads === 1 ? "atleta já descarregou" : "atletas já descarregaram"}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Cover — right side on desktop */}
          <div className="relative order-2">
            <div
              className="absolute -inset-4 rounded-sm bg-yellow-400/20 blur-2xl"
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => openWith(BOOKLET_PDF, "Booklet Hyrox Completo")}
              className="relative block group w-full text-left"
              data-testid="hyrox-cover-link"
            >
              <img
                src={COVER_IMG}
                alt="Capa · Hyrox em Férias"
                className="relative w-full h-auto border border-neutral-800 shadow-[0_30px_80px_-20px_rgba(255,214,10,0.25)] transition-transform duration-500 group-hover:scale-[1.015]"
                data-testid="hyrox-cover-image"
              />
              <div
                className="absolute top-4 right-4 bg-yellow-400 text-black font-black text-[10px] tracking-[0.24em] uppercase px-3 py-1.5"
                data-testid="hyrox-cover-ribbon"
              >
                Grátis · Novo
              </div>
            </button>
          </div>
        </div>
      </section>

      <DownloadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={onSuccess}
        selectedFile={selectedFile}
        selectedLabel={selectedLabel}
      />

      <SuccessToast
        show={showToast}
        downloads={downloads}
        onClose={() => setShowToast(false)}
      />
    </>
  );
}
